import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, Send, Paperclip, Download, Check, Clock, MessageSquare, FileText, Image as ImageIcon,
  Plus, ArrowUp, ArrowDown, Trash2, Lightbulb, Eye, DollarSign,
  Palette, LayoutTemplate, MessagesSquare, FileEdit, ClipboardCheck, FolderOpen,
  TrendingUp, ScrollText, Lock, Upload, ChevronDown, ChevronRight, AlertCircle, Mail,
  Maximize2, Languages, Pencil, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete } from "../../lib/api";
import { langName } from "../../lib/constants";
import { AdminTabs, AdminTabsHint, toggleAdminTab } from "../AdminTabs";
import ProjectSectionViewer, { SectionScroll, RequestModal, attachWheelScroll } from "./ProjectSectionViewer";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

const REQ_STATUS = {
  en_attente: { l: "En attente", c: "text-amber-300 bg-amber-400/15" },
  acceptee: { l: "Acceptée", c: "text-green-400 bg-green-500/15" },
  acceptee_modifiee: { l: "Acceptée & adaptée", c: "text-green-400 bg-green-500/15" },
  refusee: { l: "Refusée", c: "text-red-400 bg-red-500/15" },
  precisions: { l: "Précisions demandées", c: "text-blue-300 bg-blue-400/15" },
  attente: { l: "En pause", c: "text-ink-secondary bg-white/10" },
};
const VAL_STATUS = {
  demandee: { l: "Validation demandée", c: "text-amber-300 bg-amber-400/15" },
  validee: { l: "Validé", c: "text-green-400 bg-green-500/15" },
  modif_demandee: { l: "Modification demandée", c: "text-blue-300 bg-blue-400/15" },
  commentaire: { l: "Commenté", c: "text-ink-secondary bg-white/10" },
};
// ---------- Helpers fichiers (auth requise) ----------
async function openDossierFile(pid, fid, name) {
  try {
    const { data } = await api.get(`/projects/${pid}/dossier/files/${fid}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(apiErr(e)); }
}

function DossierImage({ pid, fileId, alt, className, onClick, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/projects/${pid}/dossier/files/${fileId}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [pid, fileId]);
  if (src === null) return <div className={`animate-pulse bg-white/5 ${className}`} />;
  if (!src) return <div className={`flex items-center justify-center text-ink-tertiary ${className}`}><ImageIcon size={22} /></div>;
  return <img src={src} alt={alt || ""} onClick={onClick} data-testid={testid} className={className} />;
}

// ---------- Fil de discussion ----------
// Traduction : l'admin lit les messages du porteur en français (text_fr) ; le porteur
// lit les réponses d'ACA dans sa langue (text_alt). L'original reste en infobulle.
// Glissière « Français / Langue du client » : à tout moment l'admin peut relire chaque
// échange en français ou exactement tel que le client l'a envoyé / reçu.
function Thread({ messages, onSend, placeholder, canWrite = true, testid, mode = "porteur", replyLang = null, onTranslateMsg = null, onTranslateReply = null, onChanged = null }) {
  const [text, setText] = useState("");
  const [alt, setAlt] = useState(""); // traduction vérifiée de la réponse (langue du client)
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);
  const [showFr, setShowFr] = useState(true); // glissière d'affichage (admin)
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const canTranslateReply = mode === "admin" && replyLang && replyLang !== "fr" && onTranslateReply;

  const saveEdit = async (m) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/admin/projects/${m._pid}/messages/${m.id}`, { text: editText.trim() });
      toast.success("Message modifié.");
      setEditingId(null);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const removeMsg = async (m) => {
    try {
      const r = await adminDelete(`/admin/projects/${m._pid}/messages/${m.id}`);
      if (r === null) return; // saisie annulée
      toast.success("Message supprimé.");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await onSend(text.trim(), alt.trim() || null); setText(""); setAlt(""); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };
  const translateReply = async () => {
    if (!text.trim()) return;
    setTranslating(true);
    try { setAlt(await onTranslateReply(text.trim())); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setTranslating(false); }
  };
  const translateMsg = async (m) => {
    setTranslatingId(m.id);
    try { await onTranslateMsg(m); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setTranslatingId(null); }
  };
  // Version affichée : « Français » = tout en français ; « Langue du client » = ce que
  // le client a écrit (porteur) ou reçu (text_alt des réponses ACA).
  const shownText = (m) => {
    if (mode !== "admin") return m.role === "admin" && m.text_alt ? m.text_alt : m.text;
    if (showFr) return m.role === "admin" ? m.text : (m.text_fr || m.text);
    return m.role === "admin" ? (m.text_alt || m.text) : m.text;
  };
  return (
    <div data-testid={testid}>
      {mode === "admin" && messages.length > 0 && (
        <div className="mb-3 flex items-center justify-end gap-2" data-testid={`${testid}-lang-toggle`}>
          <span className={`text-[10px] font-700 uppercase tracking-wider ${showFr ? "text-cyan" : "text-ink-tertiary"}`}>Français</span>
          <button onClick={() => setShowFr(!showFr)} data-testid={`${testid}-lang-switch`}
            title="Glisser pour relire chaque échange en français ou dans la langue du client"
            className="relative h-5 w-11 rounded-full border border-white/15 bg-elevated transition-colors hover:border-cyan/50">
            <span className={`absolute top-[3px] h-3.5 w-3.5 rounded-full bg-cyan transition-all duration-200 ${showFr ? "left-[3px]" : "left-[24px]"}`} />
          </button>
          <span className={`text-[10px] font-700 uppercase tracking-wider ${!showFr ? "text-cyan" : "text-ink-tertiary"}`}>Langue du client</span>
        </div>
      )}
      <div className="space-y-3">
        {messages.map((m) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "admin" ? "" : ""}`} data-testid={`msg-${m.id}`}>
            <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-700 ${m.role === "admin" ? "bg-cyan/15 text-cyan" : "bg-green-500/15 text-green-400"}`}>
              {(m.author || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-surface px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="font-600 text-white">{m.author}</span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-700 uppercase ${m.role === "admin" ? "bg-cyan/15 text-cyan" : "bg-green-500/15 text-green-400"}`}>
                  {m.role === "admin" ? "ACA" : "Porteur de projet"}
                </span>
                <span className="text-ink-tertiary">{fmtDT(m.at)}</span>
                {/* Admin : modifier / supprimer chaque message */}
                {mode === "admin" && onChanged && editingId !== m.id && (
                  <span className="ml-auto inline-flex items-center gap-1">
                    <button onClick={() => { setEditingId(m.id); setEditText(m.text || ""); }} data-testid={`msg-edit-${m.id}`}
                      title="Modifier ce message" className="rounded-lg p-1.5 text-ink-tertiary transition-colors hover:bg-cyan/10 hover:text-cyan">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => removeMsg(m)} data-testid={`msg-delete-${m.id}`}
                      title="Supprimer ce message (mot de passe de suppression demandé)" className="rounded-lg p-1.5 text-ink-tertiary transition-colors hover:bg-red-500/10 hover:text-red-400">
                      <Trash2 size={12} />
                    </button>
                  </span>
                )}
              </div>
              {editingId === m.id && (
                <div className="mt-2 space-y-2" data-testid={`msg-edit-form-${m.id}`}>
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={8} data-testid={`msg-edit-input-${m.id}`}
                    className="w-full resize-y rounded-xl border border-cyan/40 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-cyan" style={{ minHeight: "180px" }} />
                  {m.role === "admin" && m.text_alt && <div className="text-[10px] italic text-ink-tertiary">La version dans la langue du client sera automatiquement re-traduite à l'enregistrement.</div>}
                  <div className="flex gap-2">
                    <button onClick={() => setEditingId(null)} data-testid={`msg-edit-cancel-${m.id}`} className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-600 text-ink-secondary hover:text-white">Annuler</button>
                    <button onClick={() => saveEdit(m)} data-testid={`msg-edit-save-${m.id}`} className="rounded-full bg-cyan px-4 py-1 text-[11px] font-700 text-void hover:-translate-y-0.5">Enregistrer</button>
                  </div>
                </div>
              )}
              {m.text && editingId !== m.id && (
                <>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ink-secondary"
                     title={mode === "admin" ? (showFr ? `Langue du client : ${m.role === "admin" ? (m.text_alt || m.text) : m.text}` : `Français : ${m.role === "admin" ? m.text : (m.text_fr || m.text)}`)
                                                 : (m.role === "admin" && m.text_alt ? `Original : ${m.text}` : undefined)}>
                    {shownText(m)}
                    {m.edited_at && <span className="ml-2 text-[10px] italic text-ink-tertiary">(modifié)</span>}
                  </p>
                  {mode === "admin" && m.role !== "admin" && m.text_fr && showFr && (
                    <div className="mt-1 text-[10px] italic text-ink-tertiary" data-testid={`msg-translated-${m.id}`}>Traduit de « {langName(m.lang)} » — glissière ci-dessus pour voir l'original</div>
                  )}
                  {mode === "admin" && m.role === "admin" && m.text_alt && !showFr && (
                    <div className="mt-1 text-[10px] italic text-ink-tertiary" data-testid={`msg-translated-${m.id}`}>Version reçue par le client ({langName(m.alt_lang)})</div>
                  )}
                  {mode === "admin" && m.role !== "admin" && !m.text_fr && onTranslateMsg && (
                    <button onClick={() => translateMsg(m)} disabled={translatingId === m.id} data-testid={`msg-translate-${m.id}`}
                      className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-cyan/40 bg-cyan/10 px-2.5 py-0.5 text-[10px] font-700 uppercase tracking-wider text-cyan transition-colors hover:bg-cyan hover:text-void disabled:opacity-50">
                      <Languages size={10} /> {translatingId === m.id ? "Traduction…" : "Traduire en français"}
                    </button>
                  )}
                  {mode !== "admin" && m.role === "admin" && m.text_alt && (
                    <div className="mt-1 text-[10px] italic text-ink-tertiary" data-testid={`msg-translated-${m.id}`}>Traduction automatique — message rédigé en français par ACA (survolez pour l'original)</div>
                  )}
                </>
              )}
              {m.file_id && (
                <button onClick={() => openDossierFile(m._pid, m.file_id, m.file_name)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-[11px] text-cyan transition-colors hover:border-cyan/50">
                  <Paperclip size={11} /> {m.file_name || "Pièce jointe"}
                </button>
              )}
            </div>
          </div>
        ))}
        {messages.length === 0 && <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-center text-xs text-ink-tertiary">Aucun message pour le moment.</p>}
      </div>
      {canWrite && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder={placeholder}
              data-testid={`${testid}-input`}
              className="flex-1 resize-y rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" style={{ minHeight: "260px" }} />
            <button onClick={submit} disabled={sending || !text.trim()} data-testid={`${testid}-send`}
              className="aca-btn-primary self-end"><Send size={13} /> Envoyer</button>
          </div>
          {canTranslateReply && (
            <div>
              <button onClick={translateReply} disabled={translating || !text.trim()} data-testid={`${testid}-translate-reply`}
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-[11px] font-700 uppercase tracking-wider text-cyan transition-colors hover:bg-cyan hover:text-void disabled:opacity-50">
                <Languages size={12} /> {translating ? "Traduction…" : `Traduire en ${langName(replyLang)}`}
              </button>
              {alt && (
                <div className="mt-2 rounded-xl border border-cyan/25 bg-cyan/[0.04] p-3" data-testid={`${testid}-alt-box`}>
                  <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-cyan">Version envoyée au client ({langName(replyLang)}) — modifiable avant envoi</div>
                  <textarea value={alt} onChange={(e) => setAlt(e.target.value)} rows={6} data-testid={`${testid}-alt-input`}
                    className="w-full resize-y rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-cyan" style={{ minHeight: "130px" }} />
                  <div className="mt-1 text-[10px] italic text-ink-tertiary">Si vous modifiez votre message français, pensez à retraduire.</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Sections documentaires (Concept / Vision / Économie) ----------
// Affichage, édition, défilement, plein écran, brouillon/publication et notifications
// sont assurés par le composant commun ProjectSectionViewer (./ProjectSectionViewer).

// ---------- Demandes de modification ----------
// ================================================================================
// COMPOSANT PRINCIPAL — Dossier de suivi de projet (admin + porteur)
// ================================================================================
export default function ProjectDossier({ pid, mode, project }) {
  const isAdmin = mode === "admin";
  const apiBase = isAdmin ? `/admin/projects/${pid}/dossier` : `/projects/${pid}/dossier`;
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false); // panneau d'onglet en plein écran

  const load = useCallback(() => {
    api.get(apiBase).then((r) => setD(r.data)).catch((e) => setErr(apiErr(e)));
    if (isAdmin) api.get("/admin/project-statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
  }, [apiBase, isAdmin]);
  useEffect(() => { load(); }, [load]);

  // injecte pid dans les messages pour les pièces jointes
  const msgs = (list) => (list || []).map((m) => ({ ...m, _pid: pid }));

  if (err) return null;
  if (!d) return <div className="mt-12 border border-white/10 bg-surface p-8 text-center text-sm text-ink-tertiary" data-testid="dossier-loading">Chargement du dossier de suivi…</div>;
  if (!d.active) {
    return (
      <div className="mt-12 rounded-2xl border border-dashed border-white/15 bg-surface p-8 text-center" data-testid="dossier-locked">
        <Lock size={22} className="mx-auto text-ink-tertiary" />
        <p className="mt-3 text-sm font-600 text-white">Dossier de suivi de production</p>
        <p className="mt-1 text-xs text-ink-secondary">
          {isAdmin
            ? "Le dossier collaboratif s'active dès que le projet est marqué « SÉLECTIONNÉE »."
            : "Votre dossier de suivi s'ouvrira ici si votre projet est sélectionné par APP CHALLENGE ACADEMY."}
        </p>
      </div>
    );
  }

  const c = d.counters;
  const TABS = [
    { key: "ensemble", label: "Vue d'ensemble", icon: TrendingUp, color: "cyan" },
    { key: "concept", label: "Concept", icon: Lightbulb, color: "green" },
    { key: "vision", label: "Proposition ACA", icon: Eye, color: "amber" },
    { key: "economy", label: "Modèle économique", icon: DollarSign, color: "rose" },
    { key: "logo", label: "Identité & logo", icon: Palette, color: "violet" },
    { key: "maquettes", label: "Maquettes", icon: LayoutTemplate, color: "blue" },
    { key: "echanges", label: `Échanges (${c.messages})`, icon: MessagesSquare, color: "cyan" },
    { key: "demandes", label: `Demandes (${c.open_requests})`, icon: FileEdit, color: "green" },
    { key: "validations", label: `Validations (${c.pending_validations})`, icon: ClipboardCheck, color: "amber" },
    { key: "documents", label: `Documents (${c.files})`, icon: FolderOpen, color: "rose" },
    { key: "avancement", label: "Avancement", icon: TrendingUp, color: "violet" },
    { key: "historique", label: "Historique", icon: ScrollText, color: "blue" },
    ...(isAdmin ? [{ key: "notes", label: "Notes internes", icon: Lock, color: "rose" }] : []),
  ];

  // Chaque onglet peut être ouvert en PLEIN ÉCRAN (défilement complet sur toute la page).
  // Le panneau n'est pas démonté : la saisie en cours est conservée.
  const toggleTab = (k) => { toggleAdminTab(setTab)(k); setExpanded(false); };

  const tabContent = (
    <>
      {tab === "ensemble" && <Dashboard d={d} pid={pid} apiBase={apiBase} isAdmin={isAdmin} statuses={statuses} onChanged={load} goTab={setTab} />}
      {tab === "concept" && <ProjectSectionViewer pid={pid} apiBase={apiBase} mode={mode} sec={d.sections.concept} onChanged={load} />}
      {tab === "vision" && <ProjectSectionViewer pid={pid} apiBase={apiBase} mode={mode} sec={d.sections.vision} onChanged={load} />}
      {tab === "economy" && <ProjectSectionViewer pid={pid} apiBase={apiBase} mode={mode} sec={d.sections.economy} onChanged={load} />}
      {tab === "logo" && <Visuals pid={pid} apiBase={apiBase} isAdmin={isAdmin} visuals={d.visuals.filter((v) => v.kind === "logo")} kind="logo" title="Logo provisoire de l'application" onChanged={load} onZoom={setLightbox} />}
      {tab === "maquettes" && <Visuals pid={pid} apiBase={apiBase} isAdmin={isAdmin} visuals={d.visuals.filter((v) => v.kind === "maquette")} kind="maquette" title="Maquette / première page de l'application" onChanged={load} onZoom={setLightbox} />}
      {tab === "echanges" && (
        <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid="dossier-thread-main">
          <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Fil de discussion du projet</div>
          <Thread testid="dossier-thread" mode={mode} messages={msgs(d.messages.filter((m) => !m.section_key && !m.request_id && !m.validation_id))}
            placeholder={isAdmin ? "Message au porteur de projet…" : "Message à APP CHALLENGE ACADEMY…"}
            replyLang={isAdmin ? (project?.lang || "fr") : null}
            onTranslateMsg={isAdmin ? (m) => api.post(`/admin/projects/${pid}/messages/${m.id}/translate`).then(load) : null}
            onTranslateReply={isAdmin ? (txt) => api.post(`/admin/projects/${pid}/translate-reply`, { text: txt }).then((r) => r.data.translation) : null}
            onChanged={load}
            onSend={(text, alt) => api.post(`${apiBase}/messages`, { text, ...(alt ? { text_alt: alt } : {}) }).then(load)} />
          {isAdmin && <NotifyHint />}
        </div>
      )}
      {tab === "demandes" && <Requests pid={pid} apiBase={apiBase} isAdmin={isAdmin} d={d} onChanged={load} msgs={msgs} project={project} mode={mode} />}
      {tab === "validations" && <Validations pid={pid} apiBase={apiBase} isAdmin={isAdmin} d={d} onChanged={load} />}
      {tab === "documents" && <Documents pid={pid} apiBase={apiBase} d={d} onChanged={load} />}
      {tab === "avancement" && <StepsEditor pid={pid} apiBase={apiBase} isAdmin={isAdmin} d={d} onChanged={load} />}
      {tab === "historique" && <ActivityLog items={d.activity} pid={pid} />}
      {tab === "notes" && isAdmin && <InternalNotes pid={pid} apiBase={apiBase} notes={d.notes || []} onChanged={load} />}
    </>
  );

  return (
    <div className="mt-12" data-testid="project-dossier">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-cyan">
        <ClipboardCheck size={14} /> {isAdmin ? "Suivi du projet — dossier de production" : "Suivi de mon projet"}
      </div>
      <p className="mt-2 max-w-2xl text-xs text-ink-secondary">
        {isAdmin
          ? "Dossier collaboratif complet : documents, versions, visuels, échanges, demandes, validations, étapes et journal. Rien n'est perdu."
          : "Suivez la construction de votre application avec APP CHALLENGE ACADEMY : propositions, validations, échanges et avancement."}
      </p>

      <AdminTabs testid="dossier-tabs" tab={tab} onToggle={toggleTab} tabs={TABS} />
      {!tab && <AdminTabsHint testid="dossier-tabs-hint">Cliquez sur un onglet pour dérouler une partie du dossier.</AdminTabsHint>}

      {tab && (
        <div className={expanded ? "aca-section-scroll fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-void aca-modal" : "relative mt-6"}
          ref={expanded ? attachWheelScroll : undefined} data-testid="dossier-panel">
          <div className={expanded ? "aca-dossier-paper mx-auto max-w-5xl px-4 pb-10 sm:px-6" : "aca-dossier-paper"}>
            <div className={expanded
              ? "sticky top-0 z-10 -mx-4 mb-5 flex items-center justify-between gap-3 border-b border-white/10 bg-void/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6"
              : "mb-3 flex justify-end"}>
              {expanded && (
                <span className="truncate font-display text-base font-700 text-white" data-testid="dossier-panel-title">
                  {TABS.find((t) => t.key === tab)?.label}
                </span>
              )}
              <button onClick={() => setExpanded((v) => !v)} data-testid="dossier-expand"
                className={expanded
                  ? "flex flex-shrink-0 items-center gap-2 rounded-full border-2 border-red-400/70 bg-red-500/20 px-5 py-2.5 text-sm font-700 uppercase tracking-wider text-red-200 shadow-[0_0_16px_rgba(244,63,94,0.35)] transition-colors hover:bg-red-500/40 hover:text-white"
                  : "aca-btn-ghost ml-auto border border-white/15"}>
                {expanded ? "✕ Quitter le plein écran" : <><Maximize2 size={13} /> Plein écran</>}
              </button>
            </div>
            {tabContent}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 aca-modal" onClick={() => setLightbox(null)} data-testid="dossier-lightbox">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-full rounded-xl border border-white/20 object-contain" />
        </div>
      )}

      {/* Barre d'étapes flottante : reste visible en bas de l'écran pendant TOUTE
          la lecture du dossier, du début à la fin (demande utilisateur). */}
      <div className="sticky bottom-3 z-30 mt-6" data-testid="dossier-tabs-bottom-wrap">
        <div className="rounded-2xl border border-white/10 bg-void/90 p-2 shadow-[0_12px_40px_rgba(0,0,0,0.55)] backdrop-blur-md">
          <AdminTabs testid="dossier-tabs-bottom" tab={tab} onToggle={toggleTab} tabs={TABS} />
        </div>
      </div>
    </div>
  );
}

const NotifyHint = () => (
  <p className="mt-3 text-[11px] text-ink-tertiary">
    Le porteur est notifié dans son espace. Pour un e-mail, utilisez la case « Notifier par e-mail » lors de la publication d'un document.
  </p>
);

// ---------- Vue d'ensemble ----------
function Dashboard({ d, pid, apiBase, isAdmin, statuses, onChanged, goTab }) {
  const m = d.meta;
  const currentStep = (m.steps || []).find((s) => s.current) || (m.steps || []).filter((s) => !s.done).slice(-1)[0];
  const [status, setStatus] = useState(m.prod_status);
  const [progress, setProgress] = useState(m.progress);

  const saveMeta = async () => {
    try { await api.put(`${apiBase}/meta`, { prod_status: status, progress }); toast.success("Statut et avancement enregistrés."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // Actions requises selon le rôle
  const actions = [];
  d.validations.filter((v) => v.status === "demandee").forEach((v) =>
    actions.push({ level: "red", who: "Porteur", text: `Validation attendue : ${v.element}`, tab: "validations" }));
  d.requests.filter((r) => ["en_attente", "precisions"].includes(r.status)).forEach((r) =>
    actions.push({ level: "orange", who: isAdmin ? "ACA" : "Porteur", text: isAdmin ? `Répondre à la demande ${r.num} : « ${r.title} »` : `Votre demande ${r.num} est en cours d'étude par ACA`, tab: "demandes" }));
  if (!d.sections.concept.published) actions.push({ level: "orange", who: "ACA", text: "Rédiger et publier le concept de l'application", tab: "concept" });
  if (d.sections.economy.published) actions.push({ level: "green", who: "", text: "Modèle économique publié", tab: "economy" });
  const mine = isAdmin ? actions.filter((a) => a.who !== "Porteur" || a.level === "green") : actions;

  return (
    <div className="space-y-5" data-testid="dossier-dashboard">
      {/* Résumé visuel */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Statut du dossier</div>
          <div className="mt-1 font-display text-base font-700 text-cyan" data-testid="dash-status">{m.prod_status}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Avancement</div>
          <div className="mt-1 font-display text-2xl font-900 text-white" data-testid="dash-progress">{m.progress}%</div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-cyan transition-all" style={{ width: `${m.progress}%` }} />
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Étape actuelle</div>
          <div className="mt-1 font-display text-base font-700 text-white" data-testid="dash-step">{currentStep?.label || "—"}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">Dernière activité</div>
          <div className="mt-1 text-sm text-white" data-testid="dash-last">{fmtDT(m.last_activity)}</div>
        </div>
      </div>

      {/* Actions requises (filtrées par rôle) */}
      <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid="dash-actions">
        <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">
          <AlertCircle size={13} className="text-amber-400" /> Action requise
        </div>
        <div className="space-y-2">
          {mine.map((a, i) => (
            <button key={i} onClick={() => goTab(a.tab)}
              className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-elevated px-4 py-2.5 text-left text-sm transition-colors hover:border-cyan/40"
              data-testid={`dash-action-${i}`}>
              <span className={`h-2 w-2 flex-shrink-0 rounded-full ${a.level === "red" ? "bg-red-400" : a.level === "orange" ? "bg-amber-400" : "bg-green-400"}`} />
              <span className="text-white">{a.text}</span>
            </button>
          ))}
          {mine.length === 0 && <p className="text-xs text-ink-tertiary">Aucune action en attente — tout est à jour.</p>}
        </div>
      </div>

      {/* Zones porteur */}
      {!isAdmin && (
        <div className="grid gap-4 lg:grid-cols-3">
          <button onClick={() => goTab("concept")} className="rounded-xl border border-cyan/25 bg-cyan/5 p-5 text-left transition-colors hover:border-cyan/50" data-testid="dash-zone-propose">
            <div className="text-[11px] uppercase tracking-[0.15em] text-cyan">Ce qu'ACA me propose</div>
            <p className="mt-2 text-xs leading-relaxed text-ink-secondary">Concept, vision et modèle économique publiés par ACA pour votre projet.</p>
          </button>
          <button onClick={() => goTab("validations")} className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-5 text-left transition-colors hover:border-amber-400/50" data-testid="dash-zone-validate">
            <div className="text-[11px] uppercase tracking-[0.15em] text-amber-300">Ce que je dois valider</div>
            <p className="mt-2 text-xs leading-relaxed text-ink-secondary">{d.counters.pending_validations} validation(s) en attente de votre réponse.</p>
          </button>
          <button onClick={() => goTab("demandes")} className="rounded-xl border border-green-400/25 bg-green-400/5 p-5 text-left transition-colors hover:border-green-400/50" data-testid="dash-zone-requests">
            <div className="text-[11px] uppercase tracking-[0.15em] text-green-400">Mes demandes & échanges</div>
            <p className="mt-2 text-xs leading-relaxed text-ink-secondary">Proposez des modifications et échangez avec ACA. {d.counters.open_requests} demande(s) en cours.</p>
          </button>
        </div>
      )}

      {/* Pilotage admin : statut + avancement */}
      {isAdmin && (
        <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid="dash-admin-meta">
          <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Pilotage du dossier</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Statut global</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="dash-status-select"
                className="w-full rounded-xl border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan [color-scheme:dark]">
                {(statuses.length ? statuses : [m.prod_status]).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Avancement : {progress}%</span>
              <input type="range" min="0" max="100" value={progress} onChange={(e) => setProgress(Number(e.target.value))}
                data-testid="dash-progress-range" className="mt-3.5 w-full accent-cyan" />
            </label>
          </div>
          <button onClick={saveMeta} data-testid="dash-meta-save" className="aca-btn-primary mt-4">Enregistrer statut & avancement</button>
        </div>
      )}
    </div>
  );
}

// ---------- Visuels (logo / maquettes) ----------
function Visuals({ pid, apiBase, isAdmin, visuals, kind, title, onChanged, onZoom }) {
  const [comment, setComment] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const current = visuals.find((v) => v.current) || visuals[0];

  const upload = async () => {
    if (!file) return toast.error("Choisissez une image.");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("comment", comment); fd.append("file", file);
      await api.post(`${apiBase}/visuals`, fd);
      toast.success("Nouvelle version ajoutée — l'historique est conservé.");
      setFile(null); setComment(""); if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const setCurrent = async (vid) => {
    try { await api.post(`${apiBase}/visuals/${vid}/current`); toast.success("Version définie comme actuelle."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid={`dossier-visuals-${kind}`}>
      <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{title} — version actuelle</div>
          {current ? (
            <div className="rounded-xl border border-white/10 bg-surface p-4">
              <DossierImage pid={pid} fileId={current.file_id} alt={title}
                className="mx-auto max-h-72 cursor-zoom-in rounded-lg object-contain transition-transform hover:scale-[1.02]"
                testid={`visual-current-${kind}`}
                onClick={async () => {
                  const { data } = await api.get(`/projects/${pid}/dossier/files/${current.file_id}`, { responseType: "blob" });
                  onZoom(URL.createObjectURL(data));
                }} />
              <div className="mt-3 text-xs text-ink-secondary">
                Version {current.version} · ajoutée le {fmtDT(current.created_at)} par {current.author}
                {current.comment && <span className="block mt-1 text-ink-tertiary">« {current.comment} »</span>}
              </div>
              <p className="mt-1 text-[10px] text-ink-tertiary">Cliquez sur l'image pour l'agrandir.</p>
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-white/15 px-4 py-8 text-center text-xs text-ink-tertiary">Aucun visuel pour le moment.</p>
          )}
        </div>

        {isAdmin && (
          <div className="rounded-xl border border-white/10 bg-surface p-4" data-testid={`visual-upload-${kind}`}>
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Ajouter une nouvelle version</div>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-white/20 px-4 py-4 text-xs text-ink-secondary transition-colors hover:border-cyan/50">
              <Upload size={15} className="text-cyan" />
              {file ? file.name : "Choisir une image (JPG, PNG, WEBP — max 10 Mo)"}
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid={`visual-file-${kind}`}
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire de version (optionnel)"
              data-testid={`visual-comment-${kind}`}
              className="mt-3 w-full rounded-xl border border-white/10 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
            <button onClick={upload} disabled={uploading || !file} data-testid={`visual-upload-btn-${kind}`} className="aca-btn-primary mt-3 w-full">
              {uploading ? "Envoi…" : "Ajouter cette version"}
            </button>
          </div>
        )}
      </div>

      {visuals.length > 1 && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Historique des versions</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {visuals.map((v) => (
              <div key={v.id} className={`rounded-xl border p-2.5 ${v.current ? "border-cyan/50 bg-cyan/5" : "border-white/10 bg-surface"}`} data-testid={`visual-v${v.version}-${kind}`}>
                <DossierImage pid={pid} fileId={v.file_id} className="h-24 w-full cursor-zoom-in rounded-lg object-contain"
                  onClick={async () => {
                    const { data } = await api.get(`/projects/${pid}/dossier/files/${v.file_id}`, { responseType: "blob" });
                    onZoom(URL.createObjectURL(data));
                  }} />
                <div className="mt-2 text-[10px] text-ink-secondary">v{v.version} · {fmtDT(v.created_at)}</div>
                {isAdmin && !v.current && (
                  <button onClick={() => setCurrent(v.id)} data-testid={`visual-setcurrent-${v.id}`}
                    className="mt-1.5 text-[10px] font-600 uppercase text-cyan hover:underline">Définir actuelle</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Demandes de modification ----------
function Requests({ pid, apiBase, isAdmin, d, onChanged, msgs, project, mode }) {
  const [reqOpen, setReqOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState({});
  const [notify, setNotify] = useState({});
  const [notifyAdmin, setNotifyAdmin] = useState({});

  // Texte affiché dans la zone « Réponse d'ACA » : saisie en cours, sinon le
  // brouillon enregistré côté serveur (retrouvé sur tous les appareils).
  const currentNote = (r) => (note[r.id] !== undefined ? note[r.id] : (r.aca_draft || ""));
  const saveDraft = async (r) => {
    try {
      await api.put(`${apiBase}/requests/${r.id}/draft`, { text: currentNote(r) });
      toast.success("Brouillon enregistré — vous le retrouverez sur tous vos appareils.");
      setNote((s) => { const n = { ...s }; delete n[r.id]; return n; });
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const cancelDraft = (r) => {
    setNote((s) => { const n = { ...s }; delete n[r.id]; return n; });
    toast.info("Modifications annulées — retour au dernier brouillon enregistré.");
  };

  const resolve = async (rid, action) => {
    try {
      const req = d.requests.find((x) => x.id === rid);
      const noteText = note[rid] !== undefined ? note[rid] : (req?.aca_draft || "");
      await api.post(`${apiBase}/requests/${rid}/resolve`, { action, note: noteText, notify_email: !!notify[rid], notify_admin: !!notifyAdmin[rid] });
      toast.success("Réponse enregistrée et visible par le porteur."); onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3" data-testid="dossier-requests">
      {!isAdmin && (
        <button onClick={() => setReqOpen(true)} data-testid="requests-new" className="aca-btn-primary">
          <Plus size={14} /> Demander une modification
        </button>
      )}
      {d.requests.map((r) => {
        const st = REQ_STATUS[r.status] || REQ_STATUS.en_attente;
        const thread = msgs(d.messages.filter((m) => m.request_id === r.id));
        return (
          <div key={r.id} className="rounded-xl border border-white/10 bg-surface" data-testid={`request-${r.id}`}>
            <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="flex w-full flex-wrap items-center gap-3 px-4 py-3.5 text-left">
              <span className="font-mono text-[11px] text-cyan">{r.num}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-600 text-white">{r.title}</span>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-700 uppercase ${st.c}`} data-testid={`request-status-${r.id}`}>{st.l}</span>
              {openId === r.id ? <ChevronDown size={15} className="text-ink-tertiary" /> : <ChevronRight size={15} className="text-ink-tertiary" />}
            </button>
            {openId === r.id && (
              <div className="space-y-4 border-t border-white/10 p-4">
                <div className="grid gap-3 text-xs sm:grid-cols-2">
                  <div className="rounded-lg bg-elevated p-3"><div className="mb-1 text-[10px] uppercase tracking-wider text-ink-tertiary">Description</div><p className="whitespace-pre-line text-ink-secondary">{r.description || "—"}</p></div>
                  <div className="rounded-lg bg-elevated p-3"><div className="mb-1 text-[10px] uppercase tracking-wider text-ink-tertiary">Modification proposée</div><p className="whitespace-pre-line text-ink-secondary">{r.proposal || "—"}</p></div>
                  <div className="rounded-lg bg-elevated p-3"><div className="mb-1 text-[10px] uppercase tracking-wider text-ink-tertiary">Pourquoi ?</div><p className="whitespace-pre-line text-ink-secondary">{r.reason || "—"}</p></div>
                  <div className="rounded-lg bg-elevated p-3"><div className="mb-1 text-[10px] uppercase tracking-wider text-ink-tertiary">Suivi</div>
                    <p className="text-ink-secondary">{fmtDT(r.created_at)} · {r.author}</p>
                    {r.file_id && <button onClick={() => openDossierFile(pid, r.file_id, r.file_name)} className="mt-1 inline-flex items-center gap-1 text-cyan hover:underline"><Paperclip size={11} /> {r.file_name}</button>}
                    {r.resolution_note && <p className="mt-2 whitespace-pre-line text-white"><span className="text-[10px] uppercase tracking-wider text-cyan">Réponse ACA : </span>{r.resolution_note}</p>}
                  </div>
                </div>

                {isAdmin && !["acceptee", "acceptee_modifiee", "refusee"].includes(r.status) && (
                  <div className="rounded-xl border border-cyan/20 bg-cyan/5 p-4" data-testid={`request-resolve-${r.id}`}>
                    <div className="mb-2 text-[11px] uppercase tracking-wider text-cyan">Réponse d'ACA</div>
                    <textarea value={currentNote(r)} onChange={(e) => setNote((s) => ({ ...s, [r.id]: e.target.value }))} rows={10}
                      placeholder="Réponse / note visible par le porteur…" data-testid={`request-note-${r.id}`}
                      className="w-full resize-y rounded-xl border border-white/10 bg-surface px-3 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" style={{ minHeight: "220px" }} />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button onClick={() => saveDraft(r)} data-testid={`request-draft-save-${r.id}`}
                        title="Enregistrer ce texte sans l'envoyer — vous pourrez le reprendre plus tard, sur n'importe quel appareil"
                        className="inline-flex items-center gap-1.5 rounded-full border border-cyan/50 bg-cyan/10 px-4 py-1.5 text-[11px] font-700 uppercase tracking-wider text-cyan transition-colors hover:bg-cyan hover:text-void">
                        <Save size={12} /> Enregistrer le brouillon
                      </button>
                      <button onClick={() => cancelDraft(r)} data-testid={`request-draft-cancel-${r.id}`}
                        title="Annuler les modifications non enregistrées et revenir au dernier brouillon"
                        style={{ borderColor: "rgba(17,17,17,0.25)", color: "#374151" }}
                        className="inline-flex items-center gap-1.5 rounded-full border bg-transparent px-4 py-1.5 text-[11px] font-600 uppercase tracking-wider transition-colors hover:bg-black/5">
                        <X size={12} /> Annuler les modifications
                      </button>
                      {r.aca_draft && <span className="text-[10px] italic text-ink-tertiary" data-testid={`request-draft-info-${r.id}`}>Brouillon enregistré — non visible par le porteur tant qu'aucune action n'est validée</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
                        <input type="checkbox" checked={!!notify[r.id]} onChange={(e) => setNotify((s) => ({ ...s, [r.id]: e.target.checked }))} className="h-4 w-4 accent-cyan" data-testid={`request-notify-${r.id}`} />
                        <Mail size={13} className="text-cyan" /> Notifier le porteur par e-mail
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
                        <input type="checkbox" checked={!!notifyAdmin[r.id]} onChange={(e) => setNotifyAdmin((s) => ({ ...s, [r.id]: e.target.checked }))} className="h-4 w-4 accent-amber-400" data-testid={`request-notify-admin-${r.id}`} />
                        <Mail size={13} className="text-amber-300" /> Notifier le Super Administrateur par e-mail
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => resolve(r.id, "acceptee")} data-testid={`request-accept-${r.id}`} className="aca-btn-primary"><Check size={12} /> Accepter</button>
                      <button onClick={() => resolve(r.id, "acceptee_modifiee")} data-testid={`request-accept-mod-${r.id}`} className="aca-btn-secondary">Accepter & modifier</button>
                      <button onClick={() => resolve(r.id, "precisions")} data-testid={`request-precisions-${r.id}`} className="aca-btn-secondary">Demander des précisions</button>
                      <button onClick={() => resolve(r.id, "attente")} data-testid={`request-wait-${r.id}`} className="aca-btn-secondary">Mettre en attente</button>
                      <button onClick={() => resolve(r.id, "refusee")} data-testid={`request-refuse-${r.id}`} className="aca-btn-danger"><X size={12} /> Refuser</button>
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-2 text-[11px] uppercase tracking-wider text-ink-tertiary">Fil de discussion de la demande</div>
                  <Thread testid={`request-thread-${r.id}`} mode={mode} messages={thread}
                    placeholder="Répondre dans ce fil…"
                    replyLang={isAdmin ? (project?.lang || "fr") : null}
                    onTranslateMsg={isAdmin ? (m) => api.post(`/admin/projects/${pid}/messages/${m.id}/translate`).then(onChanged) : null}
                    onTranslateReply={isAdmin ? (txt) => api.post(`/admin/projects/${pid}/translate-reply`, { text: txt }).then((r) => r.data.translation) : null}
                    onChanged={onChanged}
                    onSend={(text, alt) => api.post(`${apiBase}/messages`, { text, request_id: r.id, ...(alt ? { text_alt: alt } : {}) }).then(onChanged)} />
                </div>
              </div>
            )}
          </div>
        );
      })}
      {d.requests.length === 0 && (
        <p className="rounded-xl border border-dashed border-white/15 px-4 py-6 text-center text-xs text-ink-tertiary">
          Aucune demande de modification pour le moment.
        </p>
      )}
      {reqOpen && <RequestModal pid={pid} apiBase={apiBase} sectionKey={null} sectionLabel="Général" onClose={() => setReqOpen(false)} onSent={() => { setReqOpen(false); onChanged(); }} />}
    </div>
  );
}

// ---------- Validations ----------
function Validations({ pid, apiBase, isAdmin, d, onChanged }) {
  const [element, setElement] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyAdmin, setNotifyAdmin] = useState(false);
  const [comment, setComment] = useState({});
  const [sending, setSending] = useState(false);

  const ask = async () => {
    if (!element.trim()) return toast.error("Indiquez l'élément à valider.");
    setSending(true);
    try {
      await api.post(`${apiBase}/validations`, { element: element.trim(), notify_email: notifyEmail, notify_admin: notifyAdmin });
      toast.success("Validation demandée au porteur de projet."); setElement(""); onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const respond = async (vid, action) => {
    try {
      await api.post(`${apiBase}/validations/${vid}/respond`, { action, comment: comment[vid] || "" });
      toast.success("Réponse envoyée à ACA."); onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3" data-testid="dossier-validations">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-surface p-4">
          <input value={element} onChange={(e) => setElement(e.target.value)} placeholder="Élément à faire valider (ex. Logo v2, maquette, modèle économique…)"
            data-testid="validation-element"
            className="min-w-0 flex-1 rounded-xl border border-white/10 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
            <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} className="h-4 w-4 accent-cyan" data-testid="validation-notify" />
            <Mail size={13} className="text-cyan" /> Notifier le porteur par e-mail
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
            <input type="checkbox" checked={notifyAdmin} onChange={(e) => setNotifyAdmin(e.target.checked)} className="h-4 w-4 accent-amber-400" data-testid="validation-notify-admin" />
            <Mail size={13} className="text-amber-300" /> Notifier le Super Administrateur par e-mail
          </label>
          <button onClick={ask} disabled={sending} data-testid="validation-ask" className="aca-btn-primary">Demander la validation</button>
        </div>
      )}
      {d.validations.map((v) => {
        const st = VAL_STATUS[v.status] || VAL_STATUS.demandee;
        return (
          <div key={v.id} className="rounded-xl border border-white/10 bg-surface p-4" data-testid={`validation-${v.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex-1 text-sm font-600 text-white">{v.element}</span>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-700 uppercase ${st.c}`} data-testid={`validation-status-${v.id}`}>{st.l}</span>
            </div>
            <div className="mt-1 text-[11px] text-ink-tertiary">Demandée le {fmtDT(v.requested_at)} par {v.requested_by}{v.responded_at ? ` · répondue le ${fmtDT(v.responded_at)}` : ""}</div>
            {v.comment && <p className="mt-2 rounded-lg bg-elevated p-3 text-xs text-ink-secondary">« {v.comment} »</p>}
            {!isAdmin && v.status === "demandee" && (
              <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/5 p-3">
                <textarea value={comment[v.id] || ""} onChange={(e) => setComment((s) => ({ ...s, [v.id]: e.target.value }))} rows={2}
                  placeholder="Votre commentaire (obligatoire pour demander une modification)…" data-testid={`validation-comment-${v.id}`}
                  className="w-full resize-y rounded-xl border border-white/10 bg-surface px-3 py-2 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => respond(v.id, "validee")} data-testid={`validation-accept-${v.id}`} className="aca-btn-primary"><Check size={12} /> Valider</button>
                  <button onClick={() => respond(v.id, "modif_demandee")} data-testid={`validation-modif-${v.id}`} className="aca-btn-secondary">Demander une modification</button>
                  <button onClick={() => respond(v.id, "commentaire")} data-testid={`validation-comment-btn-${v.id}`} className="aca-btn-secondary">Ajouter un commentaire</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {d.validations.length === 0 && <p className="rounded-xl border border-dashed border-white/15 px-4 py-6 text-center text-xs text-ink-tertiary">Aucune validation demandée pour le moment.</p>}
    </div>
  );
}

// ---------- Documents ----------
function Documents({ pid, apiBase, d, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("context", "document");
      await api.post(`${apiBase}/files`, fd);
      toast.success("Document ajouté au dossier."); onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="space-y-3" data-testid="dossier-documents">
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-surface px-4 py-5 text-xs text-ink-secondary transition-colors hover:border-cyan/50" data-testid="documents-upload">
        <Upload size={15} className="text-cyan" />
        {uploading ? "Envoi en cours…" : "Ajouter un document au dossier (JPG, PNG, WEBP, PDF, DOC, XLS, ZIP — max 10 Mo)"}
        <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.zip"
          onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {d.files.map((f) => (
        <button key={f.id} onClick={() => openDossierFile(pid, f.id, f.name)}
          className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-surface px-4 py-3 text-left transition-colors hover:border-cyan/40"
          data-testid={`document-${f.id}`}>
          <FileText size={16} className="flex-shrink-0 text-cyan" />
          <span className="min-w-0 flex-1 truncate text-sm text-white">{f.name}</span>
          <span className="text-[11px] text-ink-tertiary">{f.author} · {fmtDT(f.at)} · {fmtSize(f.size)}</span>
          <Download size={14} className="text-ink-secondary" />
        </button>
      ))}
      {d.files.length === 0 && <p className="text-center text-xs text-ink-tertiary">Aucun document partagé pour le moment.</p>}
    </div>
  );
}

// ---------- Timeline des étapes ----------
function StepsEditor({ pid, apiBase, isAdmin, d, onChanged }) {
  const [steps, setSteps] = useState(d.meta.steps || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSteps(d.meta.steps || []); }, [d.meta.steps]);

  const mut = (i, patch) => setSteps((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, dir) => setSteps((s) => {
    const arr = [...s]; const j = i + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const save = async () => {
    setSaving(true);
    try { await api.put(`${apiBase}/steps`, { steps }); toast.success("Étapes du projet mises à jour."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="dossier-steps">
      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={s.key} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${s.current ? "border-cyan/50 bg-cyan/5" : s.done ? "border-green-500/25 bg-green-500/5" : "border-white/10 bg-surface"}`}
            data-testid={`step-${s.key}`}>
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-700 ${s.done ? "bg-green-500/20 text-green-400" : s.current ? "bg-cyan/20 text-cyan" : "bg-white/10 text-ink-tertiary"}`}>
              {s.done ? <Check size={12} /> : i + 1}
            </span>
            {isAdmin ? (
              <input value={s.label} onChange={(e) => mut(i, { label: e.target.value })} data-testid={`step-label-${i}`}
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-void px-3 py-1.5 text-sm text-white outline-none focus:border-cyan" />
            ) : (
              <span className={`flex-1 text-sm ${s.done || s.current ? "text-white" : "text-ink-secondary"}`}>{s.label}</span>
            )}
            {s.date && !isAdmin && <span className="text-[11px] text-ink-tertiary">{s.date}</span>}
            {isAdmin && (
              <span className="flex items-center gap-1.5">
                <input type="date" value={s.date || ""} onChange={(e) => mut(i, { date: e.target.value })} data-testid={`step-date-${i}`}
                  className="rounded-lg border border-white/10 bg-void px-2 py-1 text-[11px] text-white outline-none [color-scheme:dark]" />
                <label className="flex cursor-pointer items-center gap-1 text-[10px] uppercase text-ink-secondary">
                  <input type="checkbox" checked={s.done} onChange={(e) => mut(i, { done: e.target.checked })} className="h-3.5 w-3.5 accent-green-500" data-testid={`step-done-${i}`} /> Faite
                </label>
                <label className="flex cursor-pointer items-center gap-1 text-[10px] uppercase text-ink-secondary">
                  <input type="checkbox" checked={!!s.current} onChange={(e) => setSteps((arr) => arr.map((x, j) => ({ ...x, current: j === i ? e.target.checked : false })))} className="h-3.5 w-3.5 accent-cyan" data-testid={`step-current-${i}`} /> En cours
                </label>
                <button onClick={() => move(i, -1)} data-testid={`step-up-${i}`} className="p-1 text-ink-secondary hover:text-white"><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} data-testid={`step-down-${i}`} className="p-1 text-ink-secondary hover:text-white"><ArrowDown size={13} /></button>
                <button onClick={() => setSteps((arr) => arr.filter((_, j) => j !== i))} data-testid={`step-del-${i}`} className="p-1 text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
              </span>
            )}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => setSteps((s) => [...s, { key: `step-${Date.now()}`, label: "Nouvelle étape", done: false, current: false, date: null }])}
            data-testid="step-add" className="aca-btn-secondary"><Plus size={13} /> Ajouter une étape</button>
          <button onClick={save} disabled={saving} data-testid="steps-save" className="aca-btn-primary">{saving ? "Enregistrement…" : "Enregistrer les étapes"}</button>
        </div>
      )}
    </div>
  );
}

// ---------- Journal complet ----------
function ActivityLog({ items, pid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid="dossier-activity">
      <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">
        <ScrollText size={13} /> Historique du projet — complet et infalsifiable
      </div>
      <SectionScroll persistKey={`${pid}-historique`} maxHeight="480px" testid="activity-scroll" className="space-y-0 pr-1">
        {items.map((a) => (
          <div key={a.id} className="flex gap-3 border-b border-white/5 py-2.5 last:border-0" data-testid={`activity-${a.id}`}>
            <span className="w-32 flex-shrink-0 font-mono text-[10px] text-ink-tertiary">{fmtDT(a.at)}</span>
            <span className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${a.role === "admin" ? "bg-cyan" : "bg-green-400"}`} />
            <p className="text-xs leading-relaxed text-ink-secondary"><span className="text-white">{a.actor}</span> — {a.text}</p>
          </div>
        ))}
        {items.length === 0 && <p className="py-4 text-center text-xs text-ink-tertiary">Le journal démarre avec la première action du dossier.</p>}
      </SectionScroll>
    </div>
  );
}

// ---------- Notes internes ACA ----------
function InternalNotes({ pid, apiBase, notes, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await api.post(`${apiBase}/notes`, { text: text.trim() }); setText(""); toast.success("Note interne ajoutée (invisible du porteur)."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-5" data-testid="dossier-notes">
      <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-red-300">
        <Lock size={13} /> Notes internes ACA
      </div>
      <p className="mb-3 text-[11px] text-red-300/70">Visibles uniquement par les administrateurs — jamais affichées au porteur de projet.</p>
      <div className="flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Note interne…" data-testid="note-input"
          className="flex-1 resize-y rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-red-400/60" />
        <button onClick={add} disabled={saving || !text.trim()} data-testid="note-add" className="aca-btn-danger self-end">Ajouter</button>
      </div>
      <div className="mt-4 space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="rounded-lg border border-white/10 bg-surface p-3" data-testid={`note-${n.id}`}>
            <div className="text-[10px] text-ink-tertiary">{n.author} · {fmtDT(n.at)}</div>
            <p className="mt-1 whitespace-pre-line text-xs text-ink-secondary">{n.text}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-center text-xs text-ink-tertiary">Aucune note interne.</p>}
      </div>
    </div>
  );
}
